/**
 * FCM token helper — best-effort token retrieval & refresh.
 *
 * Reads the VAPID key from public/firebase-config.json (or the in-page
 * fcm-config localStorage entry). When a token is granted, the caller is
 * responsible for storing it in Firestore (`fcmTokens/{token}`) so the
 * Cloud Function can target the device.
 */
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { ensureFirebaseApp } from "./firebase";
import { getFcmConfig } from "./fcm-config";

let cached: string | null = null;

async function loadVapid(): Promise<string | null> {
  const local = getFcmConfig();
  if (local?.vapidKey) return local.vapidKey;
  try {
    const res = await fetch("/firebase-config.json", { cache: "no-store" });
    if (!res.ok) return null;
    const j = await res.json();
    return j?.vapidKey || null;
  } catch {
    return null;
  }
}

export async function ensureFcmToken(): Promise<string | null> {
  if (typeof window === "undefined") return null;
  if (cached) return cached;
  try {
    if (!(await isSupported())) return null;
    if (Notification.permission !== "granted") return null;
    const vapidKey = await loadVapid();
    if (!vapidKey) return null;
    const app = ensureFirebaseApp();
    const messaging = getMessaging(app);
    // Ensure the dedicated SW is registered so background pushes work.
    let swReg: ServiceWorkerRegistration | undefined;
    if ("serviceWorker" in navigator) {
      try {
        swReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
      } catch {
        /* ignore */
      }
    }
    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swReg,
    });
    if (token) cached = token;
    return token || null;
  } catch {
    return null;
  }
}
